'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/checkbox/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import type { CheckboxColor, CheckboxSize, CheckboxVariant } from 'BomdisoftComponents/inputs/checkbox/render';
import { type ComponentProps, type ReactNode, useId } from 'react';

export type { CheckboxColor, CheckboxSize, CheckboxVariant } from 'BomdisoftComponents/inputs/checkbox/render';

export type CheckboxProps = Omit<ComponentProps<'input'>, 'size' | 'type'> & {
  color?: CheckboxColor;
  indeterminate?: boolean;
  label?: ReactNode;
  size?: CheckboxSize;
  variant?: CheckboxVariant;
};

export const Checkbox = ({ className, color = 'primary', indeterminate, label, ref, size = 'medium', variant = 'filled', ...rest }: CheckboxProps) => {
  const generatedId = useId();
  const inputId = rest.id ?? generatedId;
  const control = (
    <span className={joinClasses('bomdisoft-checkbox', !label && className)} data-color={color} data-disabled={rest.disabled ? '' : undefined} data-size={size} data-variant={variant}>
      <input
        {...rest}
        aria-checked={indeterminate ? 'mixed' : undefined}
        className='bomdisoft-checkbox-input'
        data-indeterminate={indeterminate ? '' : undefined}
        id={inputId}
        ref={node => {
          if (node) node.indeterminate = Boolean(indeterminate);
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        type='checkbox'
      />
      <span className='bomdisoft-checkbox-box'>
        <span className='bomdisoft-checkbox-rest-bar' />
        <svg aria-hidden='true' className='bomdisoft-checkbox-check' viewBox='0 0 16 16'>
          <path d='M3.4 8.4 6.4 11.4 12.6 5.2' />
        </svg>
        <span className='bomdisoft-checkbox-bar' />
      </span>
    </span>
  );
  if (!label) return control;
  return (
    <label className={joinClasses('bomdisoft-checkbox-label', className)} data-disabled={rest.disabled ? '' : undefined} data-size={size} htmlFor={inputId}>
      {control}
      {label}
    </label>
  );
};
